import { ColDef, GridApi, GridReadyEvent, IDatasource } from 'ag-grid-community';
import { Component, OnDestroy } from '@angular/core';
import { ParseKeys, RequestQueryPayload } from '@cureskin/api-client';
import { ConnectionService } from '../../../../services/connection-service';
import { AppConfig } from '../../../../app/app.config';
import { WindowRefService } from '../../../../services/window-ref-service';

@Component({
    selector: 'investigation-list',
    templateUrl: './list.html',
    standalone: false,
})
export class ListComponent implements OnDestroy {
  data: Array<Table.Investigations['attributes']> = [];
  length: number = 0;
  gridApi: GridApi;
  dataSource: IDatasource;
  window: any;
  components: any;
  ui: { grid?: { rowModelType: string, pageSize: number } } = {};
  columnDefs: ColDef[];
  searchKey: string;

  constructor(
    private readonly conn: ConnectionService,
    public readonly appConfig: AppConfig,
    public readonly windowRef: WindowRefService,
  ) {
    this.window = windowRef.nativeWindow;
  }

  ngOnInit(): void {
    this.listViewSetup();
  }

  listViewSetup(): void {
    this.ui = { grid: { rowModelType: 'infinite', pageSize: 100 } };
    this.setUpGrid();
  }

  setUpGrid(): void {
    this.dataSource = {
      rowCount: null,
      getRows: (params: any): void => {
        this.loadData(params)
          .then((data: Array<any>) => {
            if (params.startRow === 0 && !data.length) this.gridApi.showNoRowsOverlay();
            else this.gridApi.hideOverlay();
            params.successCallback(data, data.length === this.ui.grid.pageSize ? -1 : params.startRow + data.length);
          });
      },
    };
    this.components = {
      loadingRenderer(params: any): any {
        if (params.value) return params.value;
        return '';
      },
    };
    this.columnDefs = [
      {
        headerName: 'Edit',
        width: 50,
        cellRenderer: (params: any): any => {
          if (!params.data) return '';
          const id = params.data.objectId;
          const eDiv = this.window.document.createElement('div');
          eDiv.innerHTML = `<a class='fa fa-pencil-square-o' href='/products/investigations/${id}/edit'></a>`;
          return eDiv;
        },
      },
      {
        headerName: 'Name',
        width: 300,
        cellRenderer: (params: any): any => {
          if (!params.data) {
            const eDiv = this.windowRef.nativeWindow.document.createElement('div');
            eDiv.innerHTML = 'Loading...';
            return eDiv;
          }
          const eDiv = this.window.document.createElement('div');
          eDiv.innerHTML = `<a href='/products/investigations/${params.data.objectId}'>${params.data.name}</a>`;
          return eDiv;
        },
      },
      {
        headerName: 'Tests',
        width: 400,
        cellRenderer: (params: any): any => {
          if (!params.data) return '';
          const tests: Array<{ test: string, required: boolean }> = params.data.tests || [];
          return tests.map((t: { test: string, required: boolean }) => t.test).join(', ');
        },
      },
    ];
  }

  reset(): void {
    if (!this.gridApi) return;
    this.gridApi.setGridOption('datasource', this.dataSource);
  }

  resetFilters(): void {
    delete this.searchKey;
    this.reset();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setGridOption('defaultColDef', { width: 120 });
    this.gridApi.setGridOption('columnDefs', this.columnDefs);
    this.gridApi.setGridOption('cacheBlockSize', this.ui.grid.pageSize);
    this.gridApi.setGridOption('animateRows', true);
    this.gridApi.setGridOption('datasource', this.dataSource);
    this.reset();
  }

  async loadData(params: any): Promise<any> {
    const requestPayload: RequestQueryPayload<Table.Investigations> = {
      where: {},
      limit: this.ui.grid.pageSize,
      skip: params.startRow,
    };
    const { colId, sort }: { colId: ParseKeys<Table.Investigations>, sort: 'asc' | 'desc' } = params.sortModel[0]
      || { colId: 'updatedAt', sort: 'desc' };
    if (sort === 'desc') {
      requestPayload.descending = colId;
    } else {
      requestPayload.ascending = colId;
    }
    if (this.searchKey) {
      requestPayload.where.name = { $regex: this.searchKey, $options: 'i' } as any;
    }
    const data: Array<Table.Investigations['attributes']> = await this.conn.fetchInvestigationsList(params.startRow, this.ui.grid.pageSize);
    this.length = data.length;
    return data;
  }

  ngOnDestroy(): void {
    delete this.data;
    delete this.ui;
  }
}
